import React from "react";
import { Link } from "react-router-dom";

export default function TermsOfService() {
  const lastUpdated = "August 15, 2025";

  return (
    <main className="mx-auto max-w-3xl px-6 py-12">
      <header className="mb-8">
        <h1 className="text-3xl font-bold tracking-tight text-slate-900">Terms of Service</h1>
        <p className="mt-1 text-sm text-slate-500">Last updated {lastUpdated}</p>
      </header>

      <section className="prose prose-slate max-w-none">
        <h2>1. Agreement</h2>
        <p>
          These Terms of Service (“Terms”) govern your access to and use of the Frontera websites,
          applications, and services (the “Services”) provided by <strong>Frontera</strong>
          (“Frontera,” “we,” “us,” or “our”). By accessing or using the Services, you agree to these
          Terms. If you do not agree, do not use the Services.
        </p>

        <h2>2. Eligibility</h2>
        <p>
          You must be at least 13 years old (or the age required by your jurisdiction) to use the
          Services. If you use the Services on behalf of an organization, you represent that you
          have authority to bind that organization and that it accepts these Terms.
        </p>

        <h2>3. Your account</h2>
        <ul>
          <li>You must provide accurate information and keep it up to date.</li>
          <li>You are responsible for maintaining the confidentiality of your login credentials and for all activities under your account.</li>
          <li>We may require email verification and may refuse or remove usernames that impersonate others or violate these Terms.</li>
          <li>Notify us immediately of unauthorized use: <a className="text-sky-700 hover:underline" href="mailto:support@frontera.app">support@frontera.app</a>.</li>
        </ul>

        <h2>4. Acceptable use</h2>
        <p>You agree not to misuse the Services. For example, you will not:</p>
        <ul>
          <li>Break the law or infringe others’ rights.</li>
          <li>Upload or transmit malicious code, bots, or automated scraping without permission.</li>
          <li>Attempt to bypass security, rate limits, or access non-public areas of the Services.</li>
          <li>Harass, abuse, or harm others, or post misleading or unlawful content.</li>
          <li>Reverse engineer or decompile any part of the Services except as permitted by law.</li>
          <li>Use the Services to build a competing product.</li>
        </ul>

        <h2>5. Your content</h2>
        <p>
          You retain ownership of content you submit to the Services (“Your Content”). You grant
          Frontera a worldwide, non-exclusive, royalty-free license to host, store, reproduce,
          display, and otherwise use Your Content solely to provide and improve the Services. You
          represent that you have the rights necessary to grant this license.
        </p>

        <h2>6. Feedback</h2>
        <p>
          If you provide ideas or suggestions, you grant Frontera a perpetual, worldwide, irrevocable,
          royalty-free license to use them without restriction or compensation.
        </p>

        <h2>7. Third-party services</h2>
        <p>
          The Services may link to or integrate with third-party sites or services. We are not
          responsible for third-party services or their terms or privacy practices.
        </p>

        <h2>8. Fees</h2>
        <p>
          Some features may require payment. If applicable, you agree to pay all fees and taxes
          disclosed at purchase and authorize us or our payment processor to charge your payment
          method. <em>(If you do not charge fees, delete this section.)</em>
        </p>

        <h2>9. Beta features</h2>
        <p>
          We may offer preview or beta features. They are provided “as is,” may be modified or
          discontinued at any time, and may be subject to additional terms.
        </p>

        <h2>10. Termination</h2>
        <p>
          You may stop using the Services at any time. We may suspend or terminate your access if
          you violate these Terms or create risk or legal exposure for us. Upon termination, the
          rights granted to you under these Terms will cease, except for sections that by their
          nature should survive (e.g., intellectual property, disclaimers, limitations of liability).
        </p>

        <h2>11. Disclaimers</h2>
        <p>
          THE SERVICES ARE PROVIDED “AS IS” AND “AS AVAILABLE.” TO THE MAXIMUM EXTENT PERMITTED BY
          LAW, FRONTERA DISCLAIMS ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING MERCHANTABILITY,
          FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
        </p>

        <h2>12. Limitation of liability</h2>
        <p>
          TO THE MAXIMUM EXTENT PERMITTED BY LAW, FRONTERA AND ITS AFFILIATES WILL NOT BE LIABLE FOR
          ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF
          PROFITS, REVENUE, DATA, OR USE, EVEN IF ADVISED OF THE POSSIBILITY. OUR TOTAL LIABILITY FOR
          ALL CLAIMS RELATING TO THE SERVICES IN ANY 12-MONTH PERIOD IS LIMITED TO THE GREATER OF
          (A) THE AMOUNT YOU PAID US FOR THE SERVICES IN THAT PERIOD OR (B) USD $100.
        </p>

        <h2>13. Indemnification</h2>
        <p>
          You will indemnify and hold Frontera and its affiliates harmless from any claims, losses,
          and expenses (including attorneys’ fees) arising from your use of the Services, Your
          Content, or your violation of these Terms.
        </p>

        <h2>14. Privacy</h2>
        <p>
          Our <Link className="text-sky-700 hover:underline" to="/legal/privacy">Privacy Policy</Link> explains
          how we collect and use your information. You agree that we can use such information in
          accordance with the Privacy Policy.
        </p>

        <h2>15. Changes to the Services or Terms</h2>
        <p>
          We may modify the Services and these Terms from time to time. If we make material changes,
          we will provide notice (for example, by email or in-app). The updated Terms will be
          effective when posted unless stated otherwise. Your continued use of the Services after
          changes become effective means you accept the revised Terms.
        </p>

        <h2>16. Governing law; venue</h2>
        <p>
          These Terms are governed by the laws of <em>[Specify governing law, e.g., the State of
          Delaware, USA]</em>, excluding its conflict of laws rules. You and Frontera agree to the
          exclusive jurisdiction and venue of the courts located in <em>[Specify venue]</em> for any
          dispute arising out of or relating to these Terms or the Services.
        </p>

        <h2>17. Miscellaneous</h2>
        <ul>
          <li><strong>Export controls.</strong> You must comply with applicable export and sanctions laws.</li>
          <li><strong>Assignment.</strong> You may not assign these Terms without our consent; we may assign them.</li>
          <li><strong>Entire agreement.</strong> These Terms constitute the entire agreement between you and Frontera regarding the Services.</li>
          <li><strong>Severability.</strong> If a provision is unenforceable, the rest remains in effect.</li>
          <li><strong>No waiver.</strong> Failure to enforce a provision is not a waiver.</li>
          <li><strong>Force majeure.</strong> We are not liable for delays or failures due to events beyond our reasonable control.</li>
          <li><strong>Notices.</strong> We may send notices to the email associated with your account or by posting to the Services.</li>
        </ul>

        <h2>18. Contact</h2>
        <p>
          Questions about these Terms? Email{" "}
          <a className="text-sky-700 hover:underline" href="mailto:support@frontera.app">
            support@frontera.app
          </a>.
        </p>

        <p className="text-xs text-slate-500 mt-6">
          This template is provided for convenience only and does not constitute legal advice.
        </p>
      </section>
    </main>
  );
}
